/**
 * clipApi.ts — CNF frontend clip-mode API client (Sub-spec J, Block J-C).
 *
 * Provides:
 *  - setTarget(): POST /api/clip/target → returns { pairingToken, expiresAt }
 *                 then posts the cnf-clip-pair message to the extension and
 *                 awaits the cnf-clip-pair-ack.
 *
 * The route lives at /api/clip/... so this client roots at
 * REACT_APP_API_URL (= "<origin>/api") — no doubled /api prefix.
 *
 * Security:
 *  - postMessage targetOrigin = window.location.origin (NOT '*')
 *  - shopId / userId are server-derived; never sent from the frontend
 */

import axios from 'axios';
import { getTokens, saveTokens, clearTokens } from './authApi';
import { logger } from '../utils/logger';

export type ClipTargetField =
  | 'vin'
  | 'dtc'
  | 'year'
  | 'make'
  | 'model'
  | 'part_number'
  | 'description'
  | 'customer_notes'
  | 'internal_notes'
  | 'problem_description'
  | 'work_performed';

export interface SetTargetResult {
  pairingToken: string;
  expiresAt: string;
}

export interface ClipFilledPayload {
  recordType: 'estimate' | 'work_order';
  recordId: number;
  targetField: ClipTargetField;
  value: string;
}

// ── Axios client ─────────────────────────────────────────────────────────────
// REACT_APP_API_URL = "<origin>/api". The clip routes live at /api/clip/...
// so we root at REACT_APP_API_URL and call paths like "/clip/target".
const clipAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
  timeout: 10000,
});

clipAxios.interceptors.request.use(
  (config) => {
    const tokens = getTokens();
    if (tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

clipAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;
      try {
        const tokens = getTokens();
        if (tokens?.refreshToken) {
          const refreshResp = await axios.post(
            `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/refresh`,
            { refreshToken: tokens.refreshToken },
          );
          const { tokens: newTokens } = refreshResp.data;
          saveTokens(newTokens);
          originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
          return clipAxios(originalRequest);
        }
      } catch (refreshError) {
        clearTokens();
        sessionStorage.setItem('session_expired', 'true');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    if (error.response) {
      logger.error('Clip API error', { status: error.response.status, url: originalRequest?.url });
    }
    return Promise.reject(error);
  },
);

// ── Field human labels (mirrors FIELD_SPECS on the backend) ──────────────────
export const CLIP_FIELD_LABELS: Record<ClipTargetField, string> = {
  vin: 'VIN',
  dtc: 'Diagnostic Trouble Code',
  year: 'Vehicle Year',
  make: 'Vehicle Make',
  model: 'Vehicle Model',
  part_number: 'Part Number',
  description: 'Description',
  customer_notes: 'Customer Notes',
  internal_notes: 'Internal Notes',
  problem_description: 'Problem Description',
  work_performed: 'Work Performed',
};

// ── Extension postMessage ─────────────────────────────────────────────────────

export const PAIR_ACK_TIMEOUT_MS = 3000;

/**
 * Hard ceiling on the "waiting for clip…" state (WP-06 Part 1).
 *
 * MUST stay in sync with CLIP_TTL_MINUTES in backend/src/services/ClipService.ts:
 * once the clip session expires server-side, POST /clip/value is rejected, so a
 * UI that keeps waiting past this point can never be satisfied.  When this
 * elapses the UI must show a terminal failure, never keep spinning.
 */
export const CLIP_FILL_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * Send the cnf-clip-pair message to the extension content script and wait for
 * the cnf-clip-pair-ack reply.
 *
 * Returns true if the extension acknowledged, false if no ack within timeout
 * or if the extension replied ok:false (extension not installed / not active).
 */
export function sendPairMessage(
  pairingToken: string,
  label: string,
  recordType: 'estimate' | 'work_order',
  recordId: number,
): Promise<boolean> {
  return new Promise((resolve) => {
    const handler = (evt: MessageEvent) => {
      if (
        evt.origin === window.location.origin &&
        evt.data?.type === 'cnf-clip-pair-ack'
      ) {
        window.removeEventListener('message', handler);
        clearTimeout(timer);
        resolve(evt.data.ok === true);
      }
    };

    const timer = setTimeout(() => {
      window.removeEventListener('message', handler);
      resolve(false); // extension not present / not responding
    }, PAIR_ACK_TIMEOUT_MS);

    window.addEventListener('message', handler);

    window.postMessage(
      {
        type: 'cnf-clip-pair',
        token: pairingToken,
        label,
        recordType,
        recordId,
      },
      window.location.origin,
    );
  });
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Set the pending clip target and pair with the extension.
 *
 * 1. POST /api/clip/target → { pairingToken, expiresAt }
 * 2. window.postMessage the cnf-clip-pair contract to the extension
 * 3. Await cnf-clip-pair-ack (resolves true/false — false means extension absent)
 *
 * Returns { result, extensionPaired }.
 */
export async function setClipTarget(
  recordType: 'estimate' | 'work_order',
  recordId: number,
  targetField: ClipTargetField,
): Promise<{ result: SetTargetResult; extensionPaired: boolean }> {
  const resp = await clipAxios.post<SetTargetResult>('/clip/target', {
    recordType,
    recordId,
    targetField,
  });

  const result = resp.data;
  const label = CLIP_FIELD_LABELS[targetField];

  const extensionPaired = await sendPairMessage(
    result.pairingToken,
    label,
    recordType,
    recordId,
  );

  logger.log('[clip] setTarget complete', { recordType, recordId, targetField, extensionPaired });

  return { result, extensionPaired };
}
